import {default as Heap} from './Heap.js';

/**
 * Returns distance between nodes in a bidimensional plan.
 * @param {{x:Number, y:Number}} initial_node
 * @param {{x:Number, y:Number}} final_node
 * @returns {Number} Distance between nodes.
 */
export const euclidianDistance = (initial_node, final_node) => {
    return Math.hypot(initial_node.x - final_node.x, initial_node.y - final_node.y);
};

/**
 *  Calculare shortest distance between origin-goal using A* algorithm, defined by f(n) = g(n) + h(n).
 * 'n' represents a node in the path.
 * 'g(n)' is the distance traveled from the starting point to node 'n'.
 * 'h(n)' is the estimated distance (heuristic) from node 'n' to the destination. Uses euclidian heuristic: sqrt((x1 - x2)^2 + (y1 - y2)^2).
 * 'f(n)' is the total cost of a path reaching node 'n' (g(n) + h(n)). The function aims to minimize 'f(n)'.
 * 
 * @param {Array} nodes List of nodes composing the map.
 * @param {String} start_id Id for starting node.
 * @param {String} goal_id Id for goal node.
 * @returns {Array | null} Returns the path from start to goal or null if there's no path found.
 */
export const aStar = (nodes, start_id, goal_id) => {
    const by_id = new Map(nodes.map(n => [n.id, n])); // Get all ids in a (node_id - node_data) map structure.

    // If map doesn't have init and goal ids, there's no way to trace a route, return;
    if (!by_id.has(start_id) || !by_id.has(goal_id)) {
        return null;
    }

    const
        start = by_id.get(start_id),
        goal = by_id.get(goal_id),
        path_score = new Map([[start.id, 0]]),
        initial_cost = euclidianDistance(start, goal), // Initial cost to reach destiny.
        current_cost = new Map([[start.id, initial_cost]]),
        came_from = new Map(),
        open = new Heap();

    open.push({id: start.id, f: initial_cost});

    while (open.size()) {
        const {id: current_id, f} = open.pop();

        // Skip stale entries (a better path to this node was found).
        if (f > (current_cost.get(current_id) ?? Infinity)) continue;

        if (current_id == goal.id) {
            const path_ids = [];
            let cur = current_id;
            while (cur !== undefined) {
                path_ids.push(cur); cur = came_from.get(cur);
            }
            path_ids.reverse();

            return {
                path: path_ids.map(id => by_id.get(id)),
                score: current_cost.get(goal.id)
            }
        }

        const
            current_node = by_id.get(current_id),
            current_path_score = path_score.get(current_id);

        for (const neighbor_id of current_node.edges || []) {
            const neighbor = by_id.get(neighbor_id);
            if (!neighbor) {
                continue;
            }

            // Skip if neighbor is not allowed in path (except if it's the goal).
            if (neighbor.not_path && neighbor.id !== goal.id) {
                continue;
            }

            // tentative_distance is a possible improved distance compared to othres.
            const tentative_distance = current_path_score + euclidianDistance(current_node, neighbor);
            if (tentative_distance < (path_score.get(neighbor_id) ?? Infinity)) {
                came_from.set(neighbor_id, current_id);
                path_score.set(neighbor_id, tentative_distance);

                const new_cost = tentative_distance + euclidianDistance(neighbor, goal);
                current_cost.set(neighbor_id, new_cost);
                // No decreaseKey: always push; worse ones will be skipped on pop.
                open.push({id: neighbor_id, f: new_cost});
            }
        }
    }
    return null;
};

const getSegmentDirection = (segment) => {
    if (segment.length < 2) return null;
    
    const firstNode = segment[0];
    const lastNode = segment[segment.length - 1];
    
    if (firstNode.y === lastNode.y) return 'horizontal';
    if (firstNode.x === lastNode.x) return 'vertical';
    return 'diagonal';
};

const isStraightContinuation = (segment, newNode) => {
    if (segment.length < 2) return true;
    
    const firstNode = segment[0];
    const lastNode = segment[segment.length - 1];
    
    const segmentDirection = getSegmentDirection(segment);
    
    if (segmentDirection === 'horizontal') {
        return newNode.y === firstNode.y;
    } else if (segmentDirection === 'vertical') {
        return newNode.x === firstNode.x;
    }
    
    return false;
};

export const getLargestStraightSegment = (path) => {
    if (!path || !path.path || path.path.length < 2) return null;
    
    let largestSegment = [];
    let currentSegment = [path.path[0]];
    
    for (let i = 1; i < path.path.length; i++) {
        const prevNode = path.path[i - 1];
        const currNode = path.path[i];
        
        if (isStraightContinuation(currentSegment, currNode)) {
            currentSegment.push(currNode);
        } else {
            if (currentSegment.length > largestSegment.length) {
                largestSegment = [...currentSegment];
            }
            currentSegment = [prevNode, currNode];
        }
    }
    
    if (currentSegment.length > largestSegment.length) {
        largestSegment = currentSegment;
    }
    
    if (largestSegment.length > 1) {
        return {
            segment: largestSegment,
            direction: getSegmentDirection(largestSegment) // Returns 'horizontal' or 'vertical'
        };
    }
    
    return null;
};